import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { coverageLimitApi } from "../../../modules/coveragelimit/api";
import { CoverageLimitFilterPageRequest, CreateUpdateCoverageLimit } from "../../../modules/coveragelimit/types";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { EntityObject } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const COVERAGE_LIMIT_QUERY_KEYS = {
  filterCoverageLimits: (payload?: CoverageLimitFilterPageRequest) =>
    payload ? ["filterCoverageLimits", payload] : ["filterCoverageLimits"],
  createCoverageLimit: () => ["createCoverageLimit"],
  updateCoverageLimit: () => ["updateCoverageLimit"],
  deleteCoverageLimit: () => ["deleteCoverageLimit"],
};

export const useFilterCoverageLimits = (payload: CoverageLimitFilterPageRequest) => {
  return useQuery({
    queryKey: COVERAGE_LIMIT_QUERY_KEYS.filterCoverageLimits(payload),
    queryFn: async () => {
      const { data } = await coverageLimitApi.filterCoverageLimits(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateCoverageLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COVERAGE_LIMIT_QUERY_KEYS.createCoverageLimit(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCoverageLimit) => {
      const { data } = await coverageLimitApi.createCoverageLimit(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COVERAGE_LIMITS);
      queryClient.invalidateQueries({ queryKey: COVERAGE_LIMIT_QUERY_KEYS.filterCoverageLimits() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCoverageLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COVERAGE_LIMIT_QUERY_KEYS.updateCoverageLimit(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCoverageLimit>) => {
      const { data } = await coverageLimitApi.updateCoverageLimit(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COVERAGE_LIMITS);
      queryClient.invalidateQueries({ queryKey: COVERAGE_LIMIT_QUERY_KEYS.filterCoverageLimits() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCoverageLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COVERAGE_LIMIT_QUERY_KEYS.deleteCoverageLimit(),
    mutationFn: (id: string) => {
      return coverageLimitApi.deleteCoverageLimit({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COVERAGE_LIMITS);
      queryClient.invalidateQueries({ queryKey: COVERAGE_LIMIT_QUERY_KEYS.filterCoverageLimits() });
      Notification.itemDeletedNotification();
    },
  });
};
